import { createSlice } from "@reduxjs/toolkit";
import {
  updateRedirect,
  fetchCampaigns,
  fetchMyDesigns,
  fetchPlaylist,
  fetchSelectedZoneContent,
  fetchSingleMyDesign,
  fetchStats,
  fetchZones,
  requestCampaign,
  updateFolderId,
  updatePlaylistActivation,
  fetchScheduledCampaigns,
  createCampaign,
  updateTimezone,
  updateCampaign,
  updateZoneLabel,
  updateMyDesignPlayerMode,
  updateTarget,
  duplicateTargets,
  deleteZone,
  createZones,
  deleteTarget,
  createTargets,
  associateImageMyDesign,
  sendNotification,
  createMyDesign,
  addOrRemoveElementInPlaylist,
  savePlaylistPosition,
} from "./myDesignsThunks";

function initializeMyDesignsState() {
  let initialState = {
    loading: false,
    myDesigns: null,
    currentDesign: null,
    crmEnabled: false,
    crmFilters: null,
    isCrmBackdropDuplicateOn: false,
    backdropRequest: {
      from: "",
      status: false,
      infos: {},
    },
    selectedZone: {
      zone: {},
      loading: false,
      disableAction: false,
      fetchingLoading: false,
      changingMod: false,
      content: null,
      playlistActivated: null,
      playlist: null,
    },
    openCreateDesign: false,
    openCreateDesignZones: false,
    createdMyDesign: {
      creatingMyDesign: false,
    },
  };
  return initialState;
}

export const myDesignsSlice = createSlice({
  name: "myDesigns",
  initialState: initializeMyDesignsState(),
  reducers: {
    playlistChangedUpdate: (state) => {
      state.playlistChanged = false;
    },
    openCreateDesign: (state) => {
      state.openCreateDesign = true;
    },
    closeCreateDesign: (state) => {
      state.openCreateDesign = false;
    },
    openCreateDesignZones: (state) => {
      state.openCreateDesignZones = true;
    },
    closeCreateDesignZones: (state) => {
      state.openCreateDesignZones = false;
    },
    crmToggleAction: (state) => {
      state.crmEnabled = !state.crmEnabled;
    },
    updateCrmFilters: (state, action) => {
      state.crmFilters = action.payload;
    },
    cleanMyDesigns: (state) => {
      state.myDesigns = [];
      state.loading = true;
    },
    openBackdrop: (state, action) => {
      state.backdropRequest = {
        from: action.payload.from,
        status: true,
        infos: action.payload.infos,
      };
    },
    closeBackdrop: (state) => {
      state.backdropRequest = {
        from: "",
        status: false,
        infos: {},
      };
    },
    duplicateCrmBackdrop: (state) => {
      state.isCrmBackdropDuplicateOn = !state.isCrmBackdropDuplicateOn;
    },
    resetDeletedDesign: (state) => {
      state.currentDesign = {
        ...state.currentDesign,
        designNull: false,
      };
    },
    cleanCampaigns: (state) => {
      let tmpCurrentDesign = { ...state.currentDesign };
      delete tmpCurrentDesign.scheduledCampaigns;
      delete tmpCurrentDesign.campaignList;
      state.currentDesign = tmpCurrentDesign;
      state.loading = false;
    },
    cleanCurrentDesignAndRedirect: (state, action) => {
      if (!(state.currentDesign && state.currentDesign.id === action.payload))
        state.currentDesign = null;
      action.payload.cb();
    },
    cleanCurrentDesign: (state) => {
      state.currentDesign = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyDesigns.pending, (state, action) => {
        state.fetchLoading = true;
        if (action.meta.arg.hasOwnProperty("reset") && action.meta.arg.reset)
          state.myDesigns = [];
      })
      .addCase(fetchMyDesigns.fulfilled, (state, action) => {
        if (action.payload.concat) {
          if (action.payload?.reset)
            state.myDesigns = action.payload.response.data;
          else {
            if (state.myDesigns)
              state.myDesigns = [
                ...state.myDesigns,
                ...action.payload.response.data,
              ];
            else state.myDesigns = [...action.payload.response.data];
          }
          state.loading = false;
          state.hasMore = action.payload.response.hasMore;
          state.offset = action.payload.response.offset;
          state.limit = action.payload.response.limit;
          state.total = action.payload.response.total;
        } else {
          state.myDesigns = action.payload.response;
          state.isSingle = action.payload.response.length === 0 ? true : false;
        }
        state.fetchLoading = false;
      })
      .addCase(fetchMyDesigns.rejected, (state) => {
        state.loading = false;
        state.myExpariences = [];
      })
      .addCase(fetchSingleMyDesign.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSingleMyDesign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          ...action.payload,
          designNull: action.payload === null ? true : false,
        };
        state.isSingle = false;
      })
      .addCase(fetchSingleMyDesign.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchZones.pending, () => {})
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          zones: action.payload,
        };
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          error: action.payload,
        };
      })
      .addCase(fetchStats.pending, () => {})
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          stats: action.payload,
        };
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          error: action.payload,
        };
      })
      .addCase(updatePlaylistActivation.pending, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: true,
        };
      })
      .addCase(updatePlaylistActivation.fulfilled, (state, action) => {
        let newZones = [...state.currentDesign.zones];
        const isActivated = action.payload.value === true ? 1 : 0;
        newZones[
          newZones.findIndex((item) => item.id === action.payload.zoneId)
        ].playlist = isActivated;
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: false,
        };
        state.currentDesign = {
          ...state.currentDesign,
          zones: newZones,
        };
        state.playlistChanged = true;
      })
      .addCase(updatePlaylistActivation.rejected, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: false,
        };
      })
      .addCase(fetchPlaylist.pending, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          fetchingLoading: true,
        };
      })
      .addCase(fetchPlaylist.fulfilled, (state, action) => {
        action.payload.sort((a, b) => (b.position < a.position ? b : a));
        state.selectedZone = {
          ...state.selectedZone,
          playlist: action.payload,
          fetchingLoading: false,
        };
      })
      .addCase(fetchPlaylist.rejected, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          fetchingLoading: false,
        };
      })
      .addCase(fetchSelectedZoneContent.pending, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          loading: true,
        };
      })
      .addCase(fetchSelectedZoneContent.fulfilled, (state, action) => {
        state.selectedZone = {
          ...state.selectedZone,
          content: action.payload,
          loading: false,
        };
      })
      .addCase(fetchSelectedZoneContent.rejected, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          loading: false,
        };
      })
      .addCase(updateFolderId.pending, (state) => {
        state.updatingFolder = true;
      })
      .addCase(updateFolderId.fulfilled, (state, action) => {
        state.updatingFolder = false;
        state.currentDesign = {
          ...state.currentDesign,
          externalId: action.payload,
        };
      })
      .addCase(updateFolderId.rejected, (state) => {
        state.updatingFolder = false;
      })
      .addCase(requestCampaign.pending, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: true,
        };
      })
      .addCase(requestCampaign.fulfilled, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
        };
      })
      .addCase(requestCampaign.rejected, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
          error: action.payload,
        };
      })
      .addCase(fetchCampaigns.pending, (state) => {
        state.loadingCampaigns = true;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          campaignList: action.payload,
        };
        state.loadingCampaigns = false;
      })
      .addCase(fetchCampaigns.rejected, (state) => {
        state.loadingCampaigns = false;
      })
      .addCase(updateRedirect.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRedirect.fulfilled, (state, action) => {
        let tmpZones = [...state.currentDesign.zones];
        const index = tmpZones.findIndex(
          (item) =>
            item.id === action.payload.infos.id &&
            item.frameId === action.payload.infos.frameId
        );
        tmpZones[index].targetUrl = action.payload.newUrl;
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          zones: tmpZones,
        };
      })
      .addCase(updateRedirect.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateTimezone.pending, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: true,
        };
      })
      .addCase(updateTimezone.fulfilled, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
        };
      })
      .addCase(updateTimezone.rejected, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
          error: action.error,
        };
      })
      .addCase(fetchScheduledCampaigns.pending, (state) => {
        state.loadingCampaigns = true;
      })
      .addCase(fetchScheduledCampaigns.fulfilled, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          scheduledCampaigns: action.payload,
        };
        state.loadingCampaigns = false;
      })
      .addCase(fetchScheduledCampaigns.rejected, (state) => {
        state.loadingCampaigns = false;
      })
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCampaign.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCampaign.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCampaign.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCampaign.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateZoneLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateZoneLabel.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateZoneLabel.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateMyDesignPlayerMode.pending, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: true,
        };
      })
      .addCase(updateMyDesignPlayerMode.fulfilled, (state, action) => {
        let currentZones = [...state.currentDesign.zones];
        currentZones[
          currentZones.findIndex((item) => item.id === action.payload.zoneId)
        ].player = action.payload.value;
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: false,
        };
        state.currentDesign = {
          ...state.currentDesign,
          zones: currentZones,
        };
      })
      .addCase(updateMyDesignPlayerMode.rejected, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          changingMod: false,
        };
      })
      .addCase(updateTarget.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateTarget.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateTarget.rejected, (state) => {
        state.updateLoading = false;
      })
      .addCase(duplicateTargets.pending, (state) => {
        state.loading = true;
      })
      .addCase(duplicateTargets.fulfilled, (state, action) => {
        const newTargets = [...state.currentDesign.targets];
        newTargets.push(action.payload.target);
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          targets: newTargets,
          zones: [...action.payload.zones],
        };
      })
      .addCase(duplicateTargets.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        const tmpZones = [...state.currentDesign.zones];
        tmpZones.splice(
          tmpZones.findIndex(
            (zone) =>
              action.payload.id === zone.id &&
              zone.label === action.payload.label
          ),
          1
        );
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          zones: tmpZones,
        };
      })
      .addCase(deleteZone.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createZones.pending, (state) => {
        state.loading = true;
      })
      .addCase(createZones.fulfilled, (state, action) => {
        state.loading = false;
        state.openCreateDesignZones = false;
        state.currentDesign = {
          ...state.currentDesign,
          zones: [...state.currentDesign.zones, ...action.payload],
        };
      })
      .addCase(createZones.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteTarget.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTarget.fulfilled, (state, action) => {
        const tmpTargets = [...state.currentDesign.targets];
        tmpTargets.splice(
          tmpTargets.findIndex(
            (target) =>
              action.payload.id === target.id &&
              target.label === action.payload.label
          ),
          1
        );
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          targets: tmpTargets,
        };
      })
      .addCase(deleteTarget.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createTargets.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTargets.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDesign = {
          ...state.currentDesign,
          zones: [...state.currentDesign.targets, ...action.payload],
        };
      })
      .addCase(createTargets.rejected, (state) => {
        state.loading = false;
      })
      .addCase(associateImageMyDesign.pending, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: true,
        };
      })
      .addCase(associateImageMyDesign.fulfilled, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: false,
        };
      })
      .addCase(associateImageMyDesign.rejected, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: false,
        };
      })
      .addCase(sendNotification.pending, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: true,
        };
      })
      .addCase(sendNotification.fulfilled, (state) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
        };
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.currentDesign = {
          ...state.currentDesign,
          loading: false,
          error: action.error,
        };
      })
      .addCase(createMyDesign.pending, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: true,
        };
      })
      .addCase(createMyDesign.fulfilled, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: false,
        };
      })
      .addCase(createMyDesign.rejected, (state) => {
        state.createdMyDesign = {
          ...state.createdMyDesign,
          creatingMyDesign: false,
        };
      })
      .addCase(addOrRemoveElementInPlaylist.pending, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          disableAction: true,
        };
      })
      .addCase(addOrRemoveElementInPlaylist.fulfilled, (state, action) => {
        let tmp = [...state.selectedZone.content];
        const value = action.payload.activated === true ? 1 : 0;
        const index = tmp.findIndex(
          (item) => action.payload.item.id === item.id
        );
        tmp[index]["playlist"] = value;
        let tmpPlaylist = [...state.selectedZone.playlist];
        if (value === 1) {
          tmpPlaylist.push({ ...action.payload.item, playlist: value });
        } else {
          const myIndex = tmpPlaylist.findIndex(
            (item) => action.payload.item.id === item.id
          );
          tmpPlaylist[myIndex].playlist = value;
          tmpPlaylist.splice(myIndex, 1);
        }

        state.selectedZone = {
          ...state.selectedZone,
          disableAction: false,
          content: tmp,
          playlist: tmpPlaylist,
        };
      })
      .addCase(addOrRemoveElementInPlaylist.rejected, (state) => {
        state.selectedZone = {
          ...state.selectedZone,
          disableAction: false,
        };
      })
      .addCase(savePlaylistPosition.pending, () => {})
      .addCase(savePlaylistPosition.fulfilled, (state, action) => {
        state.selectedZone = {
          ...state.selectedZone,
          playlist: action.payload,
        };
      })
      .addCase(savePlaylistPosition.rejected, () => {});
  },
});

// Action creators are generated for each case reducer function
export const {
  playlistChangedUpdate,
  openCreateDesign,
  closeCreateDesign,
  openCreateDesignZones,
  closeCreateDesignZones,
  crmToggleAction,
  updateCrmFilters,
  cleanMyDesigns,
  openBackdrop,
  closeBackdrop,
  duplicateCrmBackdrop,
  resetDeletedDesign,
  cleanCampaigns,
  cleanCurrentDesignAndRedirect,
  cleanCurrentDesign,
} = myDesignsSlice.actions;

export default myDesignsSlice.reducer;
